import {useState} from "react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {useCommentLikeModal} from "../../context/CommentLikeModalContext";
import ReactionComment from "../reactionComment/ReactionComment";
import updateComment from "../../api/updateComment";
import CommentAvatarList from "./CommentAvatarList";
import dateFormatter from "../../utils/dateFormat";
import {getLevel} from "../../utils/getLevel";
import CommentListUpdate from "./CommentListUpdate";
import Spinner from "../spinner/Spinner";
import CommentMessage from "./CommentMessage";
import {CommentPostCardProps} from "../../types/ComponentProps";
import CommentsModalStyles from "./CommentsModalStyles.module.css";

const CommentPostCard = ({data}: CommentPostCardProps) => {
  const queryClient = useQueryClient();
  const {openModal} = useCommentLikeModal();
  const [isOnEdit, setIsOnEdit] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [editedMessage, setEditedMessage] = useState({
    message: data?.message || "",
  });

  const levelInfo = getLevel(data?.userPoints ?? 0);
  const levelIndex = levelInfo?.index != null ? levelInfo.index + 1 : null;

  const onEditClickHandler = () => {
    setIsOnEdit(true);
  };

  const onMessageChangeHandler = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target;
    setEditedMessage((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const updateCommentMutation = useMutation({
    mutationFn: () =>
      updateComment(Number(data?.commentId ?? 0), editedMessage.message),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getPostComments"],
      });
      setIsLoadingEdit(false);
    },
    onError: (error: Error) => {
      console.error("Error liking post:", error.message);
    },
  });

  const handleSave = async () => {
    setIsLoadingEdit(true);
    await updateCommentMutation.mutateAsync();
    setIsOnEdit(false);
  };

  const handleCancel = () => {
    setEditedMessage({message: data?.message || ""});
    setIsOnEdit(false);
  };

  const onCommentModalClick = () => {
    openModal(data);
  };

  return (
    <div className={CommentsModalStyles.commentListContainerBlk}>
      <div className={CommentsModalStyles.containerCommentListBlk}>
        <div>
          <>
            {data?.userId === 1 || data?.userId === 2 ? (
              <CommentAvatarList userId={data?.userId ?? 0} />
            ) : (
              <Link to={`/profile/${data?.userId}`}>
                <CommentAvatarList userId={data?.userId ?? 0} />
              </Link>
            )}
          </>
        </div>
        <div>
          <div className={CommentsModalStyles.flexBoxBlkCommentList}>
            <div>
              <div className={CommentsModalStyles.flexBoxSectionName}>
                <>
                  {data?.userId === 1 || data?.userId === 2 ? (
                    <h3>{data?.preferredName}</h3>
                  ) : (
                    <Link
                      to={`/profile/${data?.userId}`}
                      className={CommentsModalStyles.nameHoverCardBlk}>
                      <h3>{data?.preferredName}</h3>
                    </Link>
                  )}
                </>
                <span>{data?.pronouns}</span>
              </div>
              {data?.isAdmin ? (
                <h4 className={CommentsModalStyles.levelSpanSection}>
                  Administrator
                </h4>
              ) : (
                <h4 className={CommentsModalStyles.levelSpanSection}>
                  Lv. {levelIndex} - {levelInfo?.level}
                </h4>
              )}
            </div>
            <div>
              <p>{dateFormatter(data?.createdAt as string)}</p>
            </div>

            <CommentListUpdate
              post={data}
              onEditClickHandler={onEditClickHandler}
            />
          </div>
          {isOnEdit ? (
            <div className={CommentsModalStyles.onEditSectionBlk}>
              <textarea
                className={CommentsModalStyles.editableMessageSection}
                name="message"
                value={editedMessage.message}
                onChange={onMessageChangeHandler}
                maxLength={1024}
              />
              <div className={CommentsModalStyles.onEditFooterSectionBlk}>
                <button onClick={handleCancel}>Cancel</button>
                <button onClick={handleSave}>
                  {isLoadingEdit ? (
                    <Spinner
                      variant="extra-small-spinner"
                      alignSpin="center-spinner"
                    />
                  ) : (
                    <span className="fa-solid fa-arrow-right"></span>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <p>
              <CommentMessage
                message={data?.message}
                pointsGiven={data?.pointsGiven}
                coinsGiven={data?.coinsGiven}
                recipients={data?.recipients}
                hashTagIds={data?.hashTagIds}
              />
            </p>
          )}
        </div>
      </div>
      <div className={CommentsModalStyles.reactionSectionBlk}>
        <div className={CommentsModalStyles.flexBoxBlk}>
          {data?.reactions?.length ? (
            <>
              <i
                className={`${CommentsModalStyles.thumbsUpIcon} fa-solid fa-thumbs-up`}></i>
              <p
                className={CommentsModalStyles.clickReactionModal}
                onClick={onCommentModalClick}>
                {data?.reactions?.length} person reacted to this post
              </p>
            </>
          ) : (
            <p>Be the first to react to this post</p>
          )}
        </div>
        <div>
          <ReactionComment data={data} />
        </div>
      </div>
    </div>
  );
};

export default CommentPostCard;
